import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { partitionStaticAndDynamicQuestionnaire } from '../../../../../common/side-popover-panel/utils';
import type { GenericAnnotationT, RangeSpanT } from '../../../../../common/types/types';
import { validateQuestionnaireAnswers } from '../../../../../common/utils/questionnaire-validation';
import type { ModernizedGenericAnnotation } from '../../../../../modernization-common/side-panel';
import { questionnaireDetailsMapAtom } from '../../../../../modernization-common/side-panel/atoms/questionnaire.atom';
import type { SidePanelActions } from '../../../../../modernization-common/side-panel/types';
import { calculateDynamicFieldRanges } from '../../../../labelling/components/side-panel/labelling-side-panel-utils';
import type { FrameSpanT } from '../../../../labelling/reducers/actions';
import type { AnnotationT } from '../../../../labelling/types';
import { annotationLabelsAtom, annotationMetadataAtom } from '../../../atoms/annotation-atoms';
import { EVENTS } from '../../../atoms/label-store-dispatch/events';
export type UseDynamicQuestionnaireDetectionParams = {
  questionnaireId: string;
  selectedLabel?: string;
  annotationIdToSpan: {
    [key: string]: RangeSpanT[];
  };
  dispatch: (action: SidePanelActions<AnnotationT & ModernizedGenericAnnotation>) => void;
};
export const useDynamicQuestionnaireDetection = ({
  questionnaireId,
  annotationIdToSpan,
  dispatch
}: UseDynamicQuestionnaireDetectionParams) => {
  const questionnaireDetailsMap = useAtomValue(questionnaireDetailsMapAtom);
  const questionnaireDetails = questionnaireDetailsMap[questionnaireId];
  const annotationMetadata = useAtomValue(annotationMetadataAtom);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const dynamicQuestionnaire = useMemo(() => {
    if (questionnaireDetails?.status !== 'success') return undefined;
    const {
      dynamicQuestionnaire
    } = partitionStaticAndDynamicQuestionnaire(questionnaireDetails.questionnaire);
    return dynamicQuestionnaire;
  }, [questionnaireDetails]);
  const getSelectedAnnotationFrameRange = useCallback((annotationId: string): {
    startFrameIndex: number;
    endFrameIndex: number;
  } => {
    const ranges = annotationIdToSpan[annotationId] || [];
    if (!ranges.length) return {
      startFrameIndex: 0,
      endFrameIndex: 0
    };
    const startFrameIndex = Math.min(...ranges.map(r => r.start));
    const endFrameIndex = Math.max(...ranges.map(r => r.end));
    return {
      startFrameIndex,
      endFrameIndex
    };
  }, [annotationIdToSpan]);
  const handleResetDynamicFields = useCallback((annotationId: string, frameSpan: FrameSpanT): void => {
    dispatch({
      type: EVENTS.REMOVE_DYNAMIC_ANSWERS,
      annotationId,
      frameSpan
    });
  }, [dispatch]);
  const validateDynamicAnnotation = useCallback((dynamicAnnotation: GenericAnnotationT): boolean => {
    if (!dynamicQuestionnaire) return false;
    const answers = dynamicAnnotation.metadata?.answers;
    if (!answers) return false;
    return validateQuestionnaireAnswers(dynamicQuestionnaire, answers);
  }, [dynamicQuestionnaire]);
  const updateDynamicAnnotation = useCallback((annotationId: string, dynamicAnnotation: GenericAnnotationT, {
    startFrame,
    endFrame
  }: FrameSpanT): void => {
    const metadata = annotationMetadata.get(annotationId);
    if (!metadata) {
      return;
    }
    dispatch({
      type: EVENTS.UPDATE_DYNAMIC_QUESTIONNAIRE,
      annotationId,
      answers: dynamicAnnotation.metadata?.answers ?? {},
      frameSpan: {
        start: startFrame,
        end: endFrame
      }
    });
  }, [annotationMetadata, dispatch]);
  const getDynamicAnswersRange = useCallback((annotationId: string) => {
    const metadata = annotationMetadata.get(annotationId);
    if (!metadata) {
      return [];
    }
    const annotationLabel = annotationLabels.find(l => l.title === metadata.labelId) ?? {
      title: metadata.labelId,
      style: {
        strokeColor: '',
        fillColor: '',
        fillOpacity: 0,
        strokeWidth: 0
      }
    };
    return calculateDynamicFieldRanges(metadata.dynamicAnswers ?? [], annotationId, annotationLabel, metadata.annotationType);
  }, [annotationMetadata, annotationLabels]);
  return {
    hasDynamicQuestionnaire: Boolean(dynamicQuestionnaire && dynamicQuestionnaire.topQuestionIds.length > 0),
    getSelectedAnnotationFrameRange,
    handleResetDynamicFields,
    updateDynamicAnnotation,
    getDynamicAnswersRange,
    validateDynamicAnnotation
  };
};